import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Select2WebElementType } from "src/Types/ReDoc";
import { EQAuthParamType } from "src/Types";
import { EQueueConfigType, EQueueType } from "src/Types/ReDoc/EQueue";
import { useEQDispatch, useEQSelector } from "src/Hooks";
import { ClearBtn, IconButton, TroberButton } from "src/Components";
import { CheckBox } from "src/Components/Controls";
import { EQLoginForm } from "src/Forms/LoginForm";
import { ReactComponent as LogoIcon } from "../Assets/Logo.svg";
import { SelectBox } from "src/UI";
import { damaskLogin, damaskStart, damaskGetUserPult } from "src/Slicers/eQueue/DamaskThunks";

interface ILoginModalProps {
	type: EQueueType;
	onLogin: ( isSuccess: boolean ) => void;
	setError: ( text: string, title?: string ) => void;
	onClose: () => void;
};

/** Модальное окно для входа в электронную очередь */
const LoginModal: React.FC<ILoginModalProps> = ( { type, onLogin, setError, onClose } ) => {

	const navigate = useNavigate();
	const config = useEQSelector<EQueueConfigType>( type, "config" );
	const offices = useEQSelector<Select2WebElementType[]>( type, "offices" );

	const [ isWaiting, setIsWaiting ] = useState<boolean>(false);
	const [ renderType, setRenderType ] = useState<number>(0);
	const [ saveAcc, setSaveAcc ] = useState<boolean>(true);
	const [ winNumber, setWinNumber ] = useState<string>(config?.pultId ?? "");
	const [ initFields, setInitFields ] = useState<EQAuthParamType>();

	const { dispatch, dispatchStd, saveConfig, start } = useEQDispatch( type );

	/** Инициализация полей формы входа */
	useEffect( () => {
		if ( !config ) return;
		setInitFields({
			type: type,
			login: config.login,
			password: config.password,
			pultId: config.pultId
		});
	// eslint-disable-next-line
	}, [ config ] );

	/** Инициализация поля выбора рабочего места */
	useEffect( () => {
		if ( !config ) return;
		setWinNumber(config.pultId);
	// eslint-disable-next-line
	}, [ config ] );

	/** Закрытие модального окна по клавише Esc */
	useEffect( () => {
		const onKeyDown = ( e: KeyboardEvent ) => {
			if ( e.key === "Escape" ) onClose();
		};
		document.addEventListener( "keydown", onKeyDown );
		return () => {
			document.removeEventListener( "keydown", onKeyDown );
		};
	}, [ onClose ] );

	/** Обработчик входа в систему */
	const onLoginHandler = ( values: EQAuthParamType ) => {
		setIsWaiting(true);
		if ( saveAcc || type === EQueueType.Enter || type === EQueueType.Return ) {
			saveConfig( values );
		}
		if ( type === EQueueType.Return ) {
			dispatch( "setViewMode", "inactive" );
			onLogin(true);
			setIsWaiting(false);
			return;
		}
		if ( type === EQueueType.Damask )
			onDamaskLogin();
		else
			onQueueStart( winNumber );
	};

	/** Запуск процесса входа для очереди с прямым стартом по номеру окна */
	const onQueueStart = ( pultId: string ) => {
		start( pultId ).unwrap()
		.then ( data => {
			if ( data.hasError ) {
				setError( data.errorMessage || "123", "Ошибка EO" );
				return;
			}
			if ( !!onLogin )
				onLogin( !data.hasError );
		})
		.finally( () => setIsWaiting(false) );
	};

	/** Обработчик входа для системы Damask */
	const onDamaskLogin = () => {
		dispatchStd( damaskLogin() ).unwrap()
			.then ( answer => {
				if ( !answer.hasError ) {
					dispatchStd( damaskGetUserPult() );
					setRenderType(1);
				}
				else {
					setError( answer.errorMessage || "123", "Ошибка EO" );
				}
			})
			.finally( () => setIsWaiting(false) );
	};

	/** Обработчик запуска рабочего места */
	const onStartHandler = () => {
		setIsWaiting(true);
		dispatchStd( damaskStart( winNumber ) ).unwrap()
			.then ( data => {
				if ( !!onLogin )
					onLogin( !data.hasError );
			})
			.finally( () => setIsWaiting(false) );
	};

	/** Обработчик перенаправления на страницу помощи */
	const onRedirectHandler = () => {
		onClose();
		navigate( "./help", { relative: "path" } );
	};

	/** Рендерит окно выбора рабочего места */
	const renderChoicesWindow = () => {
		return (
			<div className="eq_login__content-part">
				<p className="eq_login__title">Выбор рабочего места</p>
				<SelectBox
					options={toOptions(offices)}
					selectedValues={winNumber ? [winNumber] : []}
					onChangeSelected={(items) => setWinNumber(items[0]?.id ?? "")}
					placeholder="Выберите рабочее место..."
					allowClear
				/>
				<TroberButton block isWaiting={isWaiting} disabled={!winNumber} onClick={onStartHandler}>
					Продолжить
				</TroberButton>
				<IconButton outline onClick={onRedirectHandler}>Не удается войти? Свяжитесь со службой поддержки</IconButton>
			</div>
		);
	};

	/** Рендерит форму входа в систему */
	const renderLoginForm = () => (
		<div className="eq_login__content-part">
			<p className="eq_login__title">Войти в электронную очередь</p>
			<EQLoginForm
				isWaiting={isWaiting}
				initFields={initFields}
				onSaveHandler={onLoginHandler}
			/>
			<CheckBox value={saveAcc} setValue={setSaveAcc} name="save_acc" isBlock>Запомнить логин и пароль</CheckBox>
			<IconButton outline onClick={onRedirectHandler}>Не удается войти? Свяжитесь со службой поддержки</IconButton>
		</div>
	);

	return createPortal((
		<div className="eq_login" onClick={onClose}>
			<div className="eq_login__wrapper" onClick={(e) => e.stopPropagation()}>
				<ClearBtn className="eq_login__wrapper__closebtn" isClose onClick={onClose} />
				<header className="eq_login__header">
					<LogoIcon />
				</header>
				<div className="eq_login__content">
					{ renderType === 0 && renderLoginForm() }
					{ renderType === 1 && renderChoicesWindow() }
				</div>
			</div>
		</div>
	), document.body );
};

/** Преобразует массив элементов Select2WebElementType в формат для SelectBox */
const toOptions = ( items: Select2WebElementType[] ): Array<{ id: string; title: string }> =>
    items.map( (item: Select2WebElementType) => ({
        id: item.id,
        title: item.text || item.id
    }));

export default LoginModal;
